import React, { useMemo, useState } from "react";
import { Button, Input, Select, Tooltip } from "antd";
import {
  Copy,
  Download,
  Pencil,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import styles from "./SegmentLibrary.module.scss";

const ORIGIN_AI = "ai_discovered";
const ORIGIN_PLAYBOOK = "playbook";

const isAiOrigin = (origin) =>
  origin === ORIGIN_AI || origin === "discovered";

const isPlaybookOrigin = (origin) => origin === ORIGIN_PLAYBOOK;

export const displaySegmentName = (name) => {
  const raw = String(name || "").trim();
  if (!raw) return raw;
  if (raw === raw.toLowerCase() && !/\s/.test(raw)) {
    return `${raw.charAt(0).toUpperCase()}${raw.slice(1)} Enthusiasts`;
  }
  return raw;
};

export const computeDuplicateFlags = (segments) => {
  const counts = {};
  (segments || []).forEach((segment) => {
    const key = displaySegmentName(segment?.name).trim().toLowerCase();
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return (segments || []).map((segment) => ({
    ...segment,
    isPossibleDuplicate:
      counts[displaySegmentName(segment?.name).trim().toLowerCase()] > 1,
  }));
};

const summarizeRule = (rule) => {
  if (!rule || typeof rule !== "object") return "";
  const op = rule.op;
  if (op === "and" || op === "or") {
    const parts = (rule.rules || []).map(summarizeRule).filter(Boolean);
    return parts.join(op === "or" ? " OR " : " AND ");
  }
  if (op === "trait_eq") return `${rule.key || "trait"} = ${rule.value ?? ""}`;
  if (op === "last_event_within_days") return `last event within ${rule.days}d`;
  if (op === "last_event_older_than_days") return `last event older than ${rule.days}d`;
  if (op === "event_count") {
    return `${rule.event || "event"} ≥ ${rule.gte ?? 1}`;
  }
  if (op === "event_count_lt") {
    return `${rule.event || "event"} < ${rule.lt ?? 1}`;
  }
  if (op === "trait_contains") {
    return `${rule.key || "trait"} contains ${rule.value ?? ""}`;
  }
  if (op === "trait_gte") return `${rule.key || "trait"} ≥ ${rule.value ?? ""}`;
  if (op === "trait_lt") return `${rule.key || "trait"} < ${rule.value ?? ""}`;
  if (op === "nps_lte") return `NPS ≤ ${rule.value}`;
  if (op === "nps_gte") return `NPS ≥ ${rule.value}`;
  if (op === "sentiment_lte") return `sentiment ≤ ${rule.value}`;
  if (op === "has_voc") return "has VoC feedback";
  if (op === "ms_predicate") return "engine predicate";
  return op || "";
};

export const ruleSummaryForSegment = (segment) => {
  const rationale = String(segment?.discovery_rationale || "").trim();
  if (rationale) return rationale;
  const description = String(segment?.description || "").trim();
  if (description) return description;
  return summarizeRule(segment?.rule_ast) || "—";
};

const formatUpdated = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const OriginBadge = ({ origin, isTest, t }) => {
  const isAI = isAiOrigin(origin);
  const isPlaybook = isPlaybookOrigin(origin);
  return (
    <span className={styles.originGroup}>
      <span
        className={`${styles.origin} ${
          isPlaybook
            ? styles.originPlaybook
            : isAI
              ? styles.originAi
              : styles.originManual
        }`}
      >
        {isAI ? <Sparkles size={12} strokeWidth={2} aria-hidden="true" /> : null}
        {isPlaybook
          ? t("abs.origin_playbook", "Playbook")
          : isAI
            ? t("abs.origin_discovered", "AI-discovered")
            : t("abs.origin_manual", "Manual")}
      </span>
      {isTest ? (
        <span className={`${styles.origin} ${styles.originTest}`}>
          {t("abs.origin_test", "Test")}
        </span>
      ) : null}
    </span>
  );
};

const SegmentLibrary = ({
  segments = [],
  loading,
  exportingId,
  testMode = false,
  mode = "library",
  onOpen,
  onExport,
  onRefineInBuilder,
  onEdit,
  onDuplicate,
  onDelete,
  onRestore,
  onPermanentDelete,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [originFilter, setOriginFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const isArchived = mode === "archived";

  const flagged = useMemo(() => computeDuplicateFlags(segments), [segments]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let result = flagged.filter((segment) => {
      const label = displaySegmentName(segment.name).toLowerCase();
      const stored = String(segment.name || "").toLowerCase();
      if (needle && !label.includes(needle) && !stored.includes(needle)) {
        return false;
      }
      if (originFilter === ORIGIN_AI) return isAiOrigin(segment.origin);
      if (originFilter === ORIGIN_PLAYBOOK) return isPlaybookOrigin(segment.origin);
      if (originFilter === "manual") {
        return !isAiOrigin(segment.origin) && !isPlaybookOrigin(segment.origin);
      }
      return true;
    });
    if (sortBy === "size") {
      result = [...result].sort(
        (a, b) => (b.estimated_size ?? 0) - (a.estimated_size ?? 0)
      );
    } else if (sortBy === "name") {
      result = [...result].sort((a, b) =>
        displaySegmentName(a.name).localeCompare(displaySegmentName(b.name))
      );
    } else {
      result = [...result].sort((a, b) => {
        const left = Date.parse(a.updated_at || "") || 0;
        const right = Date.parse(b.updated_at || "") || 0;
        return right - left;
      });
    }
    return result;
  }, [flagged, query, originFilter, sortBy]);

  if (!loading && segments.length === 0) {
    return (
      <div className={styles.empty}>
        <p>
          {t(
            isArchived
              ? testMode
                ? "abs.archived_empty_test"
                : "abs.archived_empty"
              : testMode
              ? "abs.library_empty_test"
              : "abs.library_empty",
            isArchived
              ? testMode
                ? "No archived test segments. Rediscover or delete a test segment to see it here."
                : "No archived segments. Rediscover or delete a segment to see it here."
              : testMode
              ? "No test segments yet. Discover or build them while Test mode is on — they stay hidden from the live library."
              : "No segments yet. Discovered segments and ones you build will show up here."
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Search className={styles.searchIcon} size={16} strokeWidth={2} aria-hidden="true" />
          <Input
            className={styles.search}
            allowClear
            placeholder={t("abs.library_search", "Search segments…")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t("abs.library_search", "Search segments…")}
          />
        </div>
        <Select
          className={styles.filter}
          value={originFilter}
          onChange={setOriginFilter}
          options={[
            { value: "all", label: t("abs.library_origin_all", "All origins") },
            {
              value: ORIGIN_AI,
              label: t("abs.origin_discovered", "AI-discovered"),
            },
            {
              value: ORIGIN_PLAYBOOK,
              label: t("abs.origin_playbook", "Playbook"),
            },
            { value: "manual", label: t("abs.origin_manual", "Manual") },
          ]}
        />
        <Select
          className={styles.filter}
          value={sortBy}
          onChange={setSortBy}
          options={[
            { value: "recent", label: t("abs.library_sort_recent", "Recently updated") },
            { value: "size", label: t("abs.library_sort_size", "Audience size") },
            { value: "name", label: t("abs.library_sort_name", "Name (A–Z)") },
          ]}
        />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("abs.library_col_name", "Name")}</th>
              <th>{t("abs.library_col_origin", "Origin")}</th>
              <th>{t("abs.library_col_rule", "Rule")}</th>
              <th>{t("abs.library_col_audience", "Audience")}</th>
              <th>{t("abs.library_col_updated", "Updated")}</th>
              <th>{t("abs.library_col_actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length ? (
              filtered.map((segment) => {
                const ai = isAiOrigin(segment.origin);
                const engineAudience = ai && segment?.rule_ast?.op === "ms_predicate";
                return (
                  <tr key={segment.id}>
                    <td>
                      <div className={styles.nameCell}>
                        <button
                          type="button"
                          className={styles.nameButton}
                          onClick={() => onOpen?.(segment)}
                        >
                          {displaySegmentName(segment.name)}
                        </button>
                        {segment.isPossibleDuplicate ? (
                          <span
                            className={styles.dup}
                            title={t(
                              "abs.library_dup_hint",
                              "Another segment shares this name — check for overlap before acting on both. Audience-overlap detection is not available yet."
                            )}
                          >
                            {t("abs.library_dup", "Possible duplicate")}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <div className={styles.originCell}>
                        <OriginBadge
                          origin={segment.origin}
                          isTest={Boolean(segment.is_test)}
                          t={t}
                        />
                        {isArchived ? (
                          <span className={`${styles.origin} ${styles.originArchived}`}>
                            {segment.archive_reason === "rediscover"
                              ? t("abs.archived_reason_rediscover", "Replaced by Rediscover")
                              : t("abs.archived_reason_user", "Deleted")}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <span className={styles.rule} title={ruleSummaryForSegment(segment)}>
                        {ruleSummaryForSegment(segment)}
                      </span>
                    </td>
                    <td>
                      <span className={styles.audience}>
                        <Users size={14} strokeWidth={2} aria-hidden="true" />
                        {segment.estimated_size ?? 0}
                      </span>
                    </td>
                    <td className={styles.updated}>{formatUpdated(segment.updated_at)}</td>
                    <td>
                      <div className={styles.actions}>
                        <Tooltip title={t("abs.export_segment", "Export")}>
                          <Button
                            type="text"
                            size="small"
                            aria-label={t("abs.export_segment", "Export")}
                            loading={exportingId === segment.id}
                            icon={<Download size={16} strokeWidth={2} />}
                            onClick={() => onExport?.(segment)}
                          />
                        </Tooltip>
                        {isArchived ? (
                          <>
                            <Tooltip title={t("abs.archived_restore", "Restore")}>
                              <Button
                                type="text"
                                size="small"
                                aria-label={t("abs.archived_restore", "Restore")}
                                icon={<RotateCcw size={16} strokeWidth={2} />}
                                onClick={() => onRestore?.(segment)}
                              />
                            </Tooltip>
                            <Tooltip
                              title={t(
                                "abs.archived_delete",
                                "Permanently delete"
                              )}
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                aria-label={t(
                                  "abs.archived_delete",
                                  "Permanently delete"
                                )}
                                icon={<Trash2 size={16} strokeWidth={2} />}
                                onClick={() => onPermanentDelete?.(segment)}
                              />
                            </Tooltip>
                          </>
                        ) : (
                          <>
                        {ai && !engineAudience ? (
                          <Button
                            type="link"
                            size="small"
                            className={styles.refine}
                            onClick={() => onRefineInBuilder?.(segment)}
                          >
                            {t("abs.library_refine", "Refine in Builder")}
                          </Button>
                        ) : !ai ? (
                          <Tooltip title={t("abs.library_edit", "Edit")}>
                            <Button
                              type="text"
                              size="small"
                              aria-label={t("abs.library_edit", "Edit")}
                              icon={<Pencil size={16} strokeWidth={2} />}
                              onClick={() => onEdit?.(segment)}
                            />
                          </Tooltip>
                        ) : null}
                        {!ai ? (
                          <Tooltip title={t("abs.library_duplicate", "Duplicate")}>
                            <Button
                              type="text"
                              size="small"
                              aria-label={t("abs.library_duplicate", "Duplicate")}
                              icon={<Copy size={16} strokeWidth={2} />}
                              onClick={() => onDuplicate?.(segment)}
                            />
                          </Tooltip>
                        ) : null}
                        <Tooltip title={t("abs.library_delete", "Delete")}>
                          <Button
                            type="text"
                            size="small"
                            aria-label={t("abs.library_delete", "Delete")}
                            icon={<Trash2 size={16} strokeWidth={2} />}
                            onClick={() => onDelete?.(segment)}
                          />
                        </Tooltip>
                        <Tooltip
                          title={t(
                            "abs.library_activate_soon",
                            "Coming soon — push this segment to an activation channel"
                          )}
                        >
                          <span>
                            <Button type="text" size="small" disabled className={styles.activate}>
                              {t("abs.library_activate", "Activate")}
                            </Button>
                          </span>
                        </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className={styles.noMatch}>
                  {loading
                    ? t("abs.loading", "Loading segments…")
                    : t("abs.library_no_match", "No segments match this search.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* TODO: true audience-overlap detection needs backend intersection of matched people. */}
      {/* TODO: bulk select / export is a later library extension. */}
    </div>
  );
};

export default SegmentLibrary;
